import fetch from 'tools/fetch'
import { getCurrentPosition } from 'tools/geolocation'
import { currentTimeInMs } from 'tools/date'

const googleApiBaseUrl = 'https://maps.googleapis.com/maps/api'

const getAddressValue = ({ components, names = [], longName = true, countryCode }) => {
  return names
    .map((type) => components.find((com) => com.types.some(t => t === type)))
    .filter(x => x)
    .map((com) => {
      if (com.types.includes('street_number') && countryCode === 'TW' && !com['long_name'].includes('號')) {
        return `${com['long_name']}號`
      }
      return com[longName ? 'long_name' : 'short_name']
    })
    .join(' ') ||
    undefined
}

const getMapApi = ({ mapServices, apiKey, language, country: _country, countryCode: _countryCode, mapper: _mapper, getMapper = null }) => {
  // Ensure mapServices and places API are available
  if (!mapServices || !mapServices.places) {
    throw new Error('Google Maps Places API is not loaded yet. Please ensure the API is fully initialized before calling getMapApi.')
  }

  const transformResponseAddress = (address, withCountryCode = false) => {
    const components = address.address_components
    const { lat, lng } = address.geometry.location
    const { mapper, country, countryCode } = typeof getMapper === 'function' ? getMapper(address) : {
      mapper: _mapper,
      country: _country,
      countryCode: _countryCode
    }

    const transformedAddress = {
      formattedAddress: address.formatted_address,
      addressOne: getAddressValue({ components, names: mapper.addressOne, countryCode }),
      city: getAddressValue({ components, names: mapper.city }),
      state: getAddressValue({ components, names: mapper.state }),
      postal: getAddressValue({ components, names: mapper.postal }),
      country: country || getAddressValue({ components, names: mapper.country }),
      latitude: typeof lat === 'function' ? lat() : lat,
      longitude: typeof lng === 'function' ? lng() : lng,
      ...(country !== 'Taiwan'
        ? {
            addressTwoDetails: {
              buildingName: address.name,
              unitNumber: '-',
              addressType: 'building'
            }
          }
        : {})
    }

    if (withCountryCode) {
      transformedAddress.countryCode = countryCode || getAddressValue({ components, names: mapper.country, longName: false })
    }

    return transformedAddress
  }

  // https://stackoverflow.com/questions/14343965/google-places-library-without-map
  const hiddenDiv = document.createElement('div')

  const placeService = new mapServices.places.PlacesService(hiddenDiv)

  const autoCompleteService = new mapServices.places.AutocompleteService()
  const sessionToken = new mapServices.places.AutocompleteSessionToken()

  const googleGeocodeBaseUrl = `${googleApiBaseUrl}/geocode/json?key=${apiKey}`

  const googleTimezoneBaseUrl = `${googleApiBaseUrl}/timezone/json?key=${apiKey}`

  const localisationQuery = `language=${language}&region=${_countryCode}`

  const transformAddresses = (addresses) => addresses.map(transformResponseAddress)

  const getPlaceDetails = (placeId, withCountryCode = false) => {
    return new Promise((resolve, reject) => {
      placeService.getDetails({
        placeId,
        fields: [
          'address_components',
          'formatted_address',
          'geometry',
          'name',
          'place_id'
        ]
      }, (place, status) => {
        if (status === mapServices.places.PlacesServiceStatus.OK) {
          resolve(transformResponseAddress(place, withCountryCode))
        } else {
          reject(status)
        }
      })
    })
  }

  const fetchLocationByGeometricCenter = async ({ latitude, longitude }) => {
    const { results } = await fetch(`${googleGeocodeBaseUrl}&latlng=${latitude},${longitude}&${localisationQuery}&location_type=GEOMETRIC_CENTER`, {})
    // remove plus code address -> more detail about plus code https://maps.google.com/pluscodes/
    const filterResults = results.filter((item) => !item.hasOwnProperty('plus_code'))
    return (results && transformAddresses(filterResults))
  }

  const isPostalCodeExist = (addressComponent) => addressComponent.find((item) => item.types.includes('postal_code'))

  // Exported helper
  const reverseGeoCode = async ({ latitude, longitude }) => {
    const { results } = await fetch(`${googleGeocodeBaseUrl}&latlng=${latitude},${longitude}&${localisationQuery}&location_type=ROOFTOP`, {})
    if (results.length === 0) return []
    const addressComponent = results[0].address_components
    if (isPostalCodeExist(addressComponent)) {
      return (results && transformAddresses(results))
    }
    return fetchLocationByGeometricCenter({ latitude, longitude })
  }

  // Exported helper
  const getCurrentAddress = async () => {
    try {
      const { coords } = await getCurrentPosition()
      return (await reverseGeoCode(coords))[0]
    } catch (_) {
      return null
    }
  }

  // Exported helper
  const getTimezoneFromLatLon = async (latitude, longitude) => {
    try {
      const timestamp = Math.floor(currentTimeInMs() / 1000)
      const { rawOffset } = await fetch(`${googleTimezoneBaseUrl}&location=${latitude},${longitude}&timestamp=${timestamp}`, {})
      const gmtOffset = rawOffset / 3600

      if (isNaN(gmtOffset)) {
        return null
      }

      return gmtOffset
    } catch (e) {
      return null
    }
  }

  // Exported helper
  const findPlaceFromQuery = async (input, { componentRestrictions = { country: _countryCode }, withCountryCode = false } = {}) => {
    return new Promise((resolve, reject) => {
      autoCompleteService.getPlacePredictions({
        input,
        sessionToken,
        componentRestrictions,
        fields: ['place_id', 'description']
      }, async (predictions, status) => {
        if (status === mapServices.places.PlacesServiceStatus.OK) {
          // WORKAROUND: Address search input expect address object containing addressOne field to display the address.
          const formattedPredictions = predictions.map(
            ({ description, place_id: placeId }) => ({
              formattedAddress: description,
              fetchDetails: () => getPlaceDetails(placeId, withCountryCode)
            })
          )
          resolve(formattedPredictions)
        } else {
          reject(status)
        }
      })
    })
  }

  return {
    reverseGeoCode,
    getCurrentAddress,
    getTimezoneFromLatLon,
    findPlaceFromQuery,
    getPlaceDetails
  }
}

export default getMapApi
